/**
 * Platform KPI rules — usage ladder, potential, priority quadrant, health and misuse patterns.
 * Spec: thaklis-doc/documents/managers/features/01-dashboard/PLATFORM-KPI-DESIGN.md §3.
 * Pure functions so the backend (/api/platform/kpi/*) can mirror the same rules.
 * A null metric means "unknown" — rules treat it as not met, never as zero activity.
 */

export const KPI_RULES = {
  potential: { highClients: 20, highEmployees: 500, medClients: 5, medEmployees: 100 },
  habitWeeks: 3,
  fullFeatures: 4,
  quietDays: 14,
  riskDays: 30,
  renewalWarnDays: 7,
  stuckDays: 14,
  woDropPct: 50,
};

export const STAGES = [
  { key: 'signed_up', label: 'Signed up', hint: 'Account exists' },
  { key: 'set_up', label: 'Set up', hint: 'Clients, services and staff added' },
  { key: 'started', label: 'Started', hint: 'First work order and invoice' },
  { key: 'habit', label: 'Habit', hint: 'Works in Thaklis 3 of the last 4 weeks' },
  { key: 'full', label: 'Fully using', hint: 'Habit + 4 or more core features' },
];

export const FEATURES = [
  { key: 'workOrders', label: 'Work orders' },
  { key: 'quotations', label: 'Quotations' },
  { key: 'invoices', label: 'Invoices' },
  { key: 'receipts', label: 'Receipts' },
  { key: 'wallets', label: 'Wallets' },
  { key: 'accounting', label: 'Accounting' },
  { key: 'aiExtraction', label: 'AI extraction' },
  { key: 'portal', label: 'Client portal' },
];

// `tint` = how strongly the quadrant uses the theme's primary colour (more urgent = stronger),
// so the grid follows the brand and dark mode instead of a fixed red/green/blue palette.
export const QUADRANTS = {
  priority: { label: 'Priority', action: 'Book an onboarding call or training', tint: 0.16 },
  grow: { label: 'Grow', action: 'Offer an upgrade or ask for a case study', tint: 0.09 },
  steady: { label: 'Steady', action: 'Keep happy, watch for growth', tint: 0.04 },
  low_touch: { label: 'Low-touch', action: 'Send setup tips by email', tint: 0 },
};

const DAY = 86400000;
const UAE_OFFSET = 4 * 3600000; // Asia/Dubai is UTC+4 with no DST.

const daysSince = (iso, now) => (iso ? (now.getTime() - new Date(iso).getTime()) / DAY : null);
const usedFeatureCount = (c) => FEATURES.filter((f) => c.features?.[f.key]).length;

export const getStage = (c) => {
  const isSetUp = c.clients > 0 && c.hasService && c.staffCount > 1;
  if (!isSetUp) return 'signed_up';
  if (!(c.firstWorkOrderAt && c.firstInvoiceAt)) return 'set_up';
  if (!(c.weeksActiveLast4 >= KPI_RULES.habitWeeks)) return 'started';
  return usedFeatureCount(c) >= KPI_RULES.fullFeatures ? 'full' : 'habit';
};

export const getPotential = (c) => {
  if (c.clients == null && c.employees == null) return null;
  const { highClients, highEmployees, medClients, medEmployees } = KPI_RULES.potential;
  const clients = c.clients || 0;
  const employees = c.employees || 0;
  if (clients >= highClients || employees >= highEmployees) return 'high';
  if (clients >= medClients || employees >= medEmployees) return 'medium';
  return 'low';
};

export const isHighUsage = (c) => ['habit', 'full'].includes(getStage(c));

export const getQuadrant = (c) => {
  const big = ['high', 'medium'].includes(getPotential(c));
  if (isHighUsage(c)) return big ? 'grow' : 'steady';
  return big ? 'priority' : 'low_touch';
};

export const getMisuse = (c, now = new Date()) => {
  const out = [];
  const add = (key, label, action) => out.push({ key, label, action });
  if (c.woCompleted30d >= 5 && c.woInvoiced30d / c.woCompleted30d < 0.2) {
    add('billing_outside', 'Work orders finished but not invoiced', 'Train on invoicing from work orders');
  }
  if (c.invoices30d >= 5 && c.receipts30d === 0) {
    add('payments_outside', 'Invoices issued but no receipts recorded', 'Train on receipts and collections');
  }
  if (c.adminOnly30d) {
    add('admin_only', 'Only the admin is working in Thaklis', 'Help them onboard their staff');
  }
  if (c.uploads30d >= 10 && c.wo30d === 0) {
    add('file_store', 'Uploading documents but no work orders', 'Show the work order flow');
  }
  if (c.wo30d >= 10 && c.employeesOnFile != null && c.employeesOnFile < 5) {
    add('work_without_data', 'Work orders with few employees on file', 'Help them import client employees');
  }
  const stuckFor = daysSince(c.createdAt, now);
  if (c.plan?.paid && stuckFor > KPI_RULES.stuckDays && ['signed_up', 'set_up'].includes(getStage(c))) {
    add('paid_stuck', `Paying but not started after ${KPI_RULES.stuckDays} days`, 'Book an onboarding call');
  }
  return out;
};

export const getHealth = (c, now = new Date()) => {
  const red = [];
  const amber = [];
  const idle = daysSince(c.lastBusinessAt, now);
  if (idle == null) amber.push('No business activity recorded yet');
  else if (idle > KPI_RULES.riskDays) red.push(`No business activity for over ${KPI_RULES.riskDays} days`);
  else if (idle > KPI_RULES.quietDays) amber.push(`No business activity for over ${KPI_RULES.quietDays} days`);

  if (c.failedRenewal) red.push('Subscription renewal payment failed');
  if (c.woPrev4w > 0 && c.woLast4w != null && (1 - c.woLast4w / c.woPrev4w) * 100 > KPI_RULES.woDropPct) {
    red.push(`Work orders down over ${KPI_RULES.woDropPct}% vs the previous 4 weeks`);
  }
  const misuse = getMisuse(c, now);
  if (misuse.some((m) => m.key === 'paid_stuck')) red.push(misuse.find((m) => m.key === 'paid_stuck').label);

  const renewIn = c.plan?.periodEnd ? -daysSince(c.plan.periodEnd, now) : null;
  if (c.plan?.paid && c.plan.autoRenew === false && renewIn != null && renewIn >= 0 && renewIn <= KPI_RULES.renewalWarnDays) {
    amber.push(`Renewal due in ${Math.ceil(renewIn)} days, auto-renew off`);
  }
  misuse.filter((m) => m.key !== 'paid_stuck').forEach((m) => amber.push(m.label));

  if (red.length) return { level: 'red', reasons: [...red, ...amber] };
  if (amber.length) return { level: 'amber', reasons: amber };
  return { level: 'green', reasons: [] };
};

export const nextAction = (c, now = new Date()) => {
  if (c.failedRenewal) return 'Call about the failed renewal payment';
  const misuse = getMisuse(c, now);
  if (misuse.length) return misuse[0].action;
  const idle = daysSince(c.lastBusinessAt, now);
  if (idle != null && idle > KPI_RULES.quietDays) return 'Check in: why have they gone quiet?';
  return QUADRANTS[getQuadrant(c)].action;
};

export const pct = (n, d) => (d ? Math.round((n / d) * 100) : null);

// UAE business-day boundaries, returned as real instants (UTC Dates).
const uaeMidnight = (instant) => {
  const local = new Date(instant.getTime() + UAE_OFFSET);
  return new Date(Date.UTC(local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate()) - UAE_OFFSET);
};

export const PERIODS = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: '7 days' },
  { key: 'mtd', label: 'This month' },
  { key: '30d', label: '30 days' },
  { key: '90d', label: '90 days' },
];

export const periodRange = (period, now = new Date()) => {
  const today = uaeMidnight(now);
  if (period === 'mtd') {
    const local = new Date(now.getTime() + UAE_OFFSET);
    return { from: new Date(Date.UTC(local.getUTCFullYear(), local.getUTCMonth(), 1) - UAE_OFFSET), to: now };
  }
  const days = { today: 1, '7d': 7, '30d': 30, '90d': 90 }[period] || 30;
  return { from: new Date(today.getTime() - (days - 1) * DAY), to: now };
};

// ── Growth (month over month) ────────────────────────────────────────────
// stock = a running total read at month end (centers, clients, MRR);
// flow = what happened during the month (work orders, invoiced).
export const GROWTH_METRICS = [
  { key: 'centers', label: 'Typing centers', kind: 'stock', format: 'count' },
  { key: 'payingCenters', label: 'Paying centers', kind: 'stock', format: 'count' },
  { key: 'mrr', label: 'MRR', kind: 'stock', format: 'aed' },
  { key: 'clients', label: 'Clients', kind: 'stock', format: 'count' },
  { key: 'employees', label: 'Employees', kind: 'stock', format: 'count', hint: 'People managed for clients (not TC staff)' },
  { key: 'activeUsers', label: 'Active users', kind: 'stock', format: 'count', hint: 'Users active at least once in the month' },
  { key: 'workOrders', label: 'Work orders', kind: 'flow', format: 'count' },
  { key: 'invoiced', label: 'Invoiced', kind: 'flow', format: 'aed', hint: 'Invoiced by centers through Thaklis (not Thaklis revenue)' },
  { key: 'storage', label: 'Storage', kind: 'stock', format: 'bytes' },
];

const uaeMonthKey = (instant) => new Date(instant.getTime() + UAE_OFFSET).toISOString().slice(0, 7);

/** The last n UAE calendar months as 'YYYY-MM', oldest first; the last one is the current month. */
export const lastMonths = (n, now = new Date()) => {
  const local = new Date(now.getTime() + UAE_OFFSET);
  return Array.from({ length: n }, (_, i) => new Date(Date.UTC(local.getUTCFullYear(), local.getUTCMonth() - (n - 1 - i), 1))
    .toISOString().slice(0, 7));
};

/** How many of the dated records existed by the end of each month (UAE time). Null dates are skipped. */
export const cumulativeByMonth = (isoDates, months) => {
  const keys = isoDates.filter(Boolean).map((d) => uaeMonthKey(new Date(d)));
  return months.map((m) => keys.filter((k) => k <= m).length);
};

/**
 * Month-over-month change. Stocks: now vs end of last month.
 * Flows: the last two complete months, so a half-finished month never reads as a drop.
 */
export const momChange = (values, kind) => {
  const end = kind === 'flow' ? values.length - 1 : values.length;
  const current = values[end - 1] ?? null;
  const previous = values[end - 2] ?? null;
  if (current == null || previous == null) return { current, previous, delta: null, pct: null };
  return { current, previous, delta: current - previous, pct: previous ? Math.round(((current - previous) / previous) * 100) : null };
};

/** Compound monthly growth rate in % across the series, or null when it starts at zero. */
export const cmgr = (values) => {
  const first = values[0];
  const last = values[values.length - 1];
  if (!first || last == null || values.length < 2) return null;
  return (Math.pow(last / first, 1 / (values.length - 1)) - 1) * 100;
};
